import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check } from "lucide-react";

import { cn } from "@/lib/utils";
import { Criteria } from "@/types/rubric";

const MENU_WIDTH = 320;
const MENU_GAP = 8;

const isBrowser = typeof window !== "undefined" && typeof document !== "undefined";

interface RubricCriteriaQuickMenuProps {
  open: boolean;
  position: { top: number; left: number; height: number } | null;
  criteria: Criteria[];
  selectedCriteriaId: string | null;
  onSelect: (criteriaId: string) => void;
  onClose: () => void;
}

export const RubricCriteriaQuickMenu = ({
  open,
  position,
  criteria,
  selectedCriteriaId,
  onSelect,
  onClose,
}: RubricCriteriaQuickMenuProps) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const visibleCriteria = useMemo(
    () => criteria.filter((item) => !!item?.criteria?.trim()),
    [criteria]
  );

  useEffect(() => {
    if (!open) return;
    const nextIndex = Math.max(
      0,
      visibleCriteria.findIndex((item) => item.id === selectedCriteriaId)
    );
    setActiveIndex(nextIndex === -1 ? 0 : nextIndex);
  }, [open, selectedCriteriaId, visibleCriteria]);

  useEffect(() => {
    if (!open) return;

    const handleMouseDown = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
    };
  }, [open, onClose]);

  const handleSelect = useCallback(
    (criteriaId: string) => {
      onSelect(criteriaId);
      onClose();
    },
    [onClose, onSelect]
  );

  useEffect(() => {
    if (!open) return;

    const handleKeyboard = (event: KeyboardEvent) => {
      if (!visibleCriteria.length) return;
      const key = event.key;
      if (key === "ArrowDown" || key === "ArrowUp") {
        event.preventDefault();
        event.stopPropagation();
        setActiveIndex((prev) => {
          if (!visibleCriteria.length) return 0;
          const delta = key === "ArrowDown" ? 1 : -1;
          const next = (prev + delta + visibleCriteria.length) % visibleCriteria.length;
          return next;
        });
        return;
      }

      if (key === "Enter" || key === "Tab") {
        event.preventDefault();
        event.stopPropagation();
        const nextItem = visibleCriteria[activeIndex];
        if (nextItem) {
          handleSelect(nextItem.id);
        }
        return;
      }

      if (key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        onClose();
        return;
      }

      if (key.length === 1 || key === "Backspace") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyboard, true);
    return () => {
      window.removeEventListener("keydown", handleKeyboard, true);
    };
  }, [activeIndex, handleSelect, onClose, open, visibleCriteria]);

  if (!open || !position || !visibleCriteria.length || !isBrowser) {
    return null;
  }

  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const estimatedHeight = Math.min(visibleCriteria.length, 6) * 48 + 32;

  const offsetLeft = 10;
  const offsetTop = 60;

  const left = Math.max(
    16,
    Math.min(position.left, viewportWidth - MENU_WIDTH - 16)
  ) + offsetLeft;
  const preferredTop = position.top + position.height + MENU_GAP;
  const top = Math.max(16, Math.min(preferredTop, viewportHeight - estimatedHeight)) - offsetTop;

  return createPortal(
    <div
      ref={menuRef}
      className="z-50 w-[320px] rounded-lg border bg-popover shadow-lg ring-1 ring-black/5 flex flex-col overflow-hidden"
      style={{ position: "fixed", top, left }}
    >
      <div className="px-3 py-2 border-b text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">
        Select rubric criteria
      </div>
      <div className="max-h-72 overflow-y-auto py-1">
        {visibleCriteria.map((criterion, index) => {
          const isActive = index === activeIndex;
          const isSelected = criterion.id === selectedCriteriaId;
          return (
            <button
              key={criterion.id}
              type="button"
              className={cn(
                "w-full px-3 py-2 text-left text-sm flex items-start gap-2 transition-colors",
                isActive
                  ? "bg-secondary/40 text-foreground"
                  : "text-foreground hover:bg-muted/50",
                isSelected && "font-semibold"
              )}
              onMouseEnter={() => setActiveIndex(index)}
              onClick={() => handleSelect(criterion.id)}
            >
              <span className="flex-1 leading-snug">{criterion.criteria}</span>
              {isSelected && <Check className="h-4 w-4 text-primary mt-0.5" />}
            </button>
          );
        })}
      </div>
      <div className="px-3 py-2 border-t text-[11px] text-muted-foreground flex items-center justify-between uppercase">
        <span>↑ ↓ navigate</span>
        <span>Enter select • Esc close</span>
      </div>
    </div>,
    document.body
  );
};
